import React, { useState } from "react";
import { Icon } from "semantic-ui-react";
import { criterionOutcome, criterionPoints, evaluatedOutcome, utteranceUnit, CRITERION_ORANGE } from "./evaluationRules";
import { BubbleList, DetailSection, ResultCard, StatusPill, greyBox } from "../../components/reportCards";

/**
 * One 응대 스킬 criterion. Built from the same pieces as a 제품 지식 claim card
 * (components/reportCards.js) so the two tabs read as one design: the number in 평가 기준
 * orange, a soft status pill with the points, and — opened — 평가 근거 then 관련 말풍선.
 */

/** The trainee bubbles a criterion cites. Very old results stored bare strings, no ids. */
const relevantBubbles = (result) =>
  (result.relevant_utterances || []).map((u, i) =>
    typeof u === "object"
      ? { id: u.id, chatlog_pk: result.idx2clid?.[u.id] ?? u.chatlog_pk ?? "", text: result.idx2sent?.[u.id] ?? u.sentence ?? u.utterance ?? "" }
      : { id: i + 1, chatlog_pk: "", text: u }
  );

/** Admin-only (results that kept the three evaluators): each one's verdict and reasoning. */
const Evaluators = ({ result, score }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    {["evaluator_1", "evaluator_2", "evaluator_3"].map((key, i) => {
      const ev = result[key];
      if (!ev) return null;
      const outcome = criterionOutcome(score, !!ev.is_criterion_met);
      return (
        <div key={key} style={greyBox}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ fontWeight: 600, color: "#1d1d1f" }}>평가자 {i + 1}</span>
            <StatusPill favorable={outcome.favorable}>{outcome.label}</StatusPill>
            <span style={{ fontSize: "12px", color: "#86868b" }}>신뢰도 {(ev.confidence * 100).toFixed(0)}%</span>
          </div>
          {ev.thought}
        </div>
      );
    })}
  </div>
);

const EvaluationCard = ({ evaluatedcriterion: ec }) => {
  const [showEvaluators, setShowEvaluators] = useState(false);
  const result = ec.result;
  const outcome = evaluatedOutcome(ec);
  const tags = (ec.criterion.tags || "").split(",").map((t) => t.trim()).filter((t) => t && t !== "deduction");
  const points = result && result.score ? result.score : criterionPoints(ec);
  const unit = utteranceUnit([ec]);
  const bubbles = result ? relevantBubbles(result) : [];

  return (
    <ResultCard
      number={ec.criterion.int_id + 1}
      numberColor={CRITERION_ORANGE}
      title={ec.criterion.text}
      meta={tags.length ? tags.map((t) => `#${t}`).join("  ") : null}
      busy={ec.is_postprocessing}
      pill={outcome && (
        <StatusPill favorable={outcome.favorable} title={`배점: ${ec.criterion.score}점`}>
          {points} / {ec.criterion.score}
        </StatusPill>
      )}
    >
      {ec.is_postprocessed && result ? (
        <>
          <DetailSection label="평가 근거">
            <div style={{ fontSize: "14px", lineHeight: 1.6, color: "#424245" }}>{result.evaluation || outcome.sentence}</div>
          </DetailSection>
          {bubbles.length > 0 && (
            <DetailSection label={`관련 ${unit} (${bubbles.length})`}>
              <BubbleList bubbles={bubbles} unit={unit} />
            </DetailSection>
          )}
          {result.evaluator_1 !== undefined && (
            <DetailSection label="AI 평가자">
              <button
                type="button"
                onClick={() => setShowEvaluators((s) => !s)}
                style={{ border: "none", background: "none", padding: 0, color: "#007AFF", fontSize: "13px", fontWeight: 600, cursor: "pointer", marginBottom: showEvaluators ? "8px" : 0 }}
              >
                {showEvaluators ? "접기" : "평가자별 상세 보기"}
              </button>
              {showEvaluators && <Evaluators result={result} score={ec.criterion.score} />}
            </DetailSection>
          )}
        </>
      ) : (
        <div style={{ paddingTop: "14px", color: "#86868b", fontSize: "13px" }}>
          <Icon name="spinner" loading /> 평가가 진행 중입니다...
        </div>
      )}
    </ResultCard>
  );
};

export default EvaluationCard;
